import { Activity, Check, ChevronDown, ClipboardCheck, Globe2, GraduationCap, Landmark, Megaphone, PhoneCall, ShieldCheck, UserPlus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Cross-gym work lives here, not in the left nav (the left nav is only about the selected facility).
// Each item says who can see it.
const GLOBAL_ITEMS = [
  { to: "/lead-nurture", label: "Lead Nurture", hint: "Every gym's new leads, click to call", icon: PhoneCall, can: "nurture", testid: "global-lead-nurture" },
  { to: "/accounts", label: "Accounts", hint: "Bank reconciliation, refunds, purchases, salaries", icon: Landmark, can: "accounts", testid: "global-accounts" },
  { to: "/improvement", label: "Continuous Improvement", hint: "Training and practice for every team", icon: GraduationCap, can: "everyone", testid: "global-improvement" },
  { to: "/hiring", label: "Hiring", hint: "Coach applications, interviews, offers", icon: UserPlus, can: "hiring", testid: "global-hiring" },
  { to: "/sales-check", label: "Sales check", hint: "QR questionnaires, GoHighLevel shows, recordings", icon: ClipboardCheck, can: "salesCheck", testid: "global-sales-check" },
  { to: "/ad-spend", label: "Ad spend", hint: "Meta spend split across gyms by leads", icon: Megaphone, min: "admin", testid: "global-ad-spend" },
  { to: "/systems", label: "Systems", hint: "Bots, jobs, ad accounts and balances, checked daily", icon: Activity, min: "admin", testid: "global-systems" },
  { to: "/global-admin", label: "Global Admin", hint: "Manage every facility & user", icon: ShieldCheck, min: "admin", testid: "global-global-admin" },
];

// Lead Nurture: admins and the Lead Nurture team (Global Admin → Lead Nurture toggle).
export const canSeeNurture = (user) => !!user?.lead_nurture_access || !!user?.nurture_manager || atLeast(user?.role, "admin");
// Accounts: admins, the head of accounts and the accounts team (Accounts → Team).
export const canSeeAccounts = (user) => !!user?.accounts_access || !!user?.accounts_head || atLeast(user?.role, "admin");
export const isAccountsHead = (user) => !!user?.accounts_head || atLeast(user?.role, "admin");
// Sales check: admins (it reads every gym's consults and GoHighLevel calendar).
export const canSeeSalesCheck = (user) => atLeast(user?.role, "admin");
// Hiring: facility heads (managers, their own gyms' candidates) and admins.
// Hiring: managers, and Operations staff (the operations role at a gym) for those gyms
export const canSeeHiring = (user) => atLeast(user?.role, "manager") || (user?.ops_gyms || []).length > 0;
const CAN = { hiring: canSeeHiring, nurture: canSeeNurture, accounts: canSeeAccounts, salesCheck: canSeeSalesCheck, everyone: (user) => !!user };

export function useGlobalItems() {
  const { user, effectiveRole } = useAuth();
  const role = effectiveRole || user?.role;
  return GLOBAL_ITEMS.filter((i) => (i.can ? CAN[i.can](user) : atLeast(role, i.min)));
}

export function GlobalSwitcher() {
  const items = useGlobalItems();
  const loc = useLocation();
  const navigate = useNavigate();
  if (items.length === 0) return null;
  const active = items.find((i) => loc.pathname.startsWith(i.to));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={`inline-flex items-center gap-2 rounded-full border bg-[#0d0d0d] px-3 py-1.5 text-xs text-zinc-200 hover:border-amber-500/60 hover:text-zinc-50 transition-colors ${active ? "border-amber-500/50" : "border-[#262626]"}`}
        data-testid="global-switcher-trigger"
      >
        <Globe2 className="h-3.5 w-3.5 text-amber-400" />
        <div className="hidden sm:flex flex-col text-left leading-tight">
          <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Global</span>
          <span className="font-medium">{active?.label || "All gyms"}</span>
        </div>
        <ChevronDown className="h-3 w-3 text-zinc-500" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0f0f0f] border-[#262626] text-zinc-100 min-w-[240px]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Across every gym</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#1f1f1f]" />
        {items.map(({ to, label, hint, icon: Icon, testid }) => (
          <DropdownMenuItem key={to} onClick={() => navigate(to)} className="focus:bg-[#1a1a1a] cursor-pointer" data-testid={testid}>
            <Icon className="h-3.5 w-3.5 mr-2 text-amber-300" />
            <div className="flex-1">
              <div className="text-sm font-medium">{label}</div>
              <div className="text-[10px] text-zinc-500">{hint}</div>
            </div>
            {active?.to === to && <Check className="h-3.5 w-3.5 text-emerald-400" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
